import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DispatchBuilder from './pages/DispatchBuilder';
import Dispatches from './pages/Dispatches';
import DispatchView from './pages/DispatchView';
import Customers from './pages/Customers';
import CoBranding from './pages/CoBranding';
import Products from './pages/Products';
import Batches from './pages/Batches';
import Transformations from './pages/Transformations';
import WeeklyPlanner from './pages/WeeklyPlanner';
import SalesOrderEntry from './pages/SalesOrderEntry';
import PalletLabelDesigner from './pages/PalletLabelDesigner';
import Reports from './pages/Reports';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import BackupSettings from './pages/BackupSettings';
import Profile from './pages/Profile';
import DeletedItems from './pages/DeletedItems';
import QCManagement from './pages/QCManagement';
import DataManagement from './pages/DataManagement';
import CustomerProductCodes from './pages/CustomerProductCodes';
import CustomerProductFormulaPage from './pages/CustomerProductFormulaPage';
import CustomerCodeTraceReport from './pages/CustomerCodeTraceReport';
import ComplianceChecklist from './pages/ComplianceChecklist';
import TemplateDesigner from './pages/TemplateDesigner';
import DeploymentDashboard from './pages/DeploymentDashboard';
import DatabaseHost from './pages/DatabaseHost';
import ApiSettings from './pages/data-management/ApiSettings';
import SalesOrderPurge from './pages/data-management/SalesOrderPurge';
import DispatchNotePurge from './pages/data-management/DispatchNotePurge';
import OOSRemediation from './pages/OOSRemediation';
import BatchCorrections from './pages/BatchCorrections';
import FeatureTimeline from './pages/FeatureTimeline';
import AccountsIQSettings from './pages/data-management/AccountsIQSettings';
import AccountsIQSync from './pages/data-management/AccountsIQSync';
import QuickOrder from './pages/QuickOrder';
import AccountSettings from './pages/AccountSettings';
import ComplianceCenter from './pages/ComplianceCenter';
import SecurityAuditDashboard from './pages/SecurityAuditDashboard';
import RecallSearch from './pages/RecallSearch';
import LabelDesigner from './pages/LabelDesigner';
import LabelReprintManagement from './pages/LabelReprintManagement';
import UserGuide from './pages/UserGuide';
import Suppliers from './pages/Suppliers';
import StockReturns from './pages/StockReturns';
// EVENTS-PLUGIN SEAM: generic plugin router registry
import { pluginRoutes } from './plugins/registry';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children, adminOnly = false, allowDpo = false }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly) {
    const allowed = ['super_admin', 'admin'];
    if (allowDpo) allowed.push('dpo');
    if (!allowed.includes(user?.role)) {
      return <Navigate to="/" replace />;
    }
  }

  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { loading, needsSetup, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={<Login />}
      />
      <Route path="/reset-password" element={<Login />} />
      
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/quick-order" element={<ProtectedRoute><QuickOrder /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/sales-order/new" element={<ProtectedRoute><SalesOrderEntry /></ProtectedRoute>} />
      <Route path="/sales-order/:id" element={<ProtectedRoute><SalesOrderEntry /></ProtectedRoute>} />
      <Route path="/dispatches" element={<ProtectedRoute><Dispatches /></ProtectedRoute>} />
      <Route path="/returns" element={<ProtectedRoute><StockReturns /></ProtectedRoute>} />
      <Route path="/dispatch/new" element={<ProtectedRoute><DispatchBuilder /></ProtectedRoute>} />
      <Route path="/dispatch/:id" element={<ProtectedRoute><DispatchBuilder /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/cobranding" element={<ProtectedRoute><CoBranding /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/batches" element={<ProtectedRoute><Batches /></ProtectedRoute>} />
      <Route path="/qc" element={<ProtectedRoute><QCManagement /></ProtectedRoute>} />
      <Route path="/transformations" element={<ProtectedRoute><Transformations /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><WeeklyPlanner /></ProtectedRoute>} />
      <Route path="/dispatch-view" element={<ProtectedRoute><DispatchView /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplateDesigner /></ProtectedRoute>} />
      <Route path="/pallet-labels/:dispatchId" element={<ProtectedRoute><PalletLabelDesigner /></ProtectedRoute>} />
      <Route path="/pallet-labels" element={<ProtectedRoute><PalletLabelDesigner /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports/compliance" element={<ProtectedRoute><ComplianceChecklist /></ProtectedRoute>} />
      <Route path="/supplier-codes" element={<ProtectedRoute><CustomerProductCodes /></ProtectedRoute>} />
      <Route path="/supplier-codes/:mappingId/formula" element={<ProtectedRoute><CustomerProductFormulaPage /></ProtectedRoute>} />
      <Route path="/supplier-trace" element={<ProtectedRoute><CustomerCodeTraceReport /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      
      {/* Admin routes */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/settings/accountsiq-sync" element={<ProtectedRoute adminOnly><AccountsIQSync /></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute adminOnly><BackupSettings /></ProtectedRoute>} />
      <Route path="/deleted-items" element={<ProtectedRoute adminOnly><DeletedItems /></ProtectedRoute>} />
      <Route path="/admin/data-management" element={<ProtectedRoute adminOnly><DataManagement /></ProtectedRoute>} />
      <Route path="/admin/data-management/database-host" element={<ProtectedRoute adminOnly><DatabaseHost /></ProtectedRoute>} />
      <Route path="/admin/data-management/api-settings" element={<ProtectedRoute adminOnly><ApiSettings /></ProtectedRoute>} />
      <Route path="/admin/data-management/sales-order-purge" element={<ProtectedRoute adminOnly><SalesOrderPurge /></ProtectedRoute>} />
      <Route path="/admin/data-management/dispatch-note-purge" element={<ProtectedRoute adminOnly><DispatchNotePurge /></ProtectedRoute>} />
      <Route path="/admin/data-management/oos-remediation" element={<ProtectedRoute adminOnly><OOSRemediation /></ProtectedRoute>} />
      <Route path="/admin/data-management/batch-corrections" element={<ProtectedRoute adminOnly><BatchCorrections /></ProtectedRoute>} />
      <Route path="/admin/data-management/feature-timeline" element={<ProtectedRoute adminOnly><FeatureTimeline /></ProtectedRoute>} />
      <Route path="/admin/data-management/accountsiq" element={<ProtectedRoute adminOnly><AccountsIQSettings /></ProtectedRoute>} />
      <Route path="/admin/deployment" element={<ProtectedRoute adminOnly><DeploymentDashboard /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute adminOnly><ComplianceCenter /></ProtectedRoute>} />
      <Route path="/admin/security-audit" element={<ProtectedRoute adminOnly allowDpo><SecurityAuditDashboard /></ProtectedRoute>} />
      <Route path="/admin/recall" element={<ProtectedRoute><RecallSearch /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/admin/data-management/label-designer" element={<ProtectedRoute adminOnly><LabelDesigner /></ProtectedRoute>} />
      <Route path="/admin/data-management/label-reprints" element={<ProtectedRoute adminOnly><LabelReprintManagement /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />
      <Route path="/help/:slug" element={<ProtectedRoute><UserGuide /></ProtectedRoute>} />

      {/* EVENTS-PLUGIN SEAM: plugin routes (each gated by its own flag guard) */}
      {pluginRoutes.map(({ path, Component }) => (
        <Route key={path} path={path} element={<ProtectedRoute><Component /></ProtectedRoute>} />
      ))}

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
